import React, { useState, useCallback, useMemo, memo, useEffect } from 'react';
import {
    Input,
    Button,
    Card,
    Spin,
    message,
    Modal,
    Select,
    Avatar,
    Empty,
    Tag,
    Space,
    Tooltip,
    Divider,
    Row,
    Col,
    Image,
    Tabs,
    Table,
    Badge,
    Popconfirm
} from 'antd';
import {
    SearchOutlined,
    UserAddOutlined,
    MailOutlined,
    SendOutlined,
    ReloadOutlined,
    UserOutlined,
    MedicineBoxOutlined,
    ClockCircleOutlined,
    CheckCircleOutlined,
    CloseCircleOutlined,
    ExclamationCircleOutlined,
    DeleteOutlined,
    StopOutlined
} from '@ant-design/icons';
import drEasyApi from '../../../Api/Customapi';
import PageTitle from '../../Ui/PageTitle';
import { getImg } from '../../../Api/getImg';
import { useLocation } from 'react-router-dom';


const searchDoctors = async (searchParams) => {
    try {
        const params = {};
        if (searchParams.name) params.Name = searchParams.name;

        const response = await drEasyApi.get('/api/Doctor/SearchDcotor', { params });
        return response.data;
    } catch (error) {
        console.error('Error searching doctors:', error);
        throw new Error('Failed to search doctors. Please try again.');
    }
};

const sendInvitation = async (doctorId) => {
    try {
        const response = await drEasyApi.post('/api/Invitations/sendInvitation', null, {
            params: { DoctorId: doctorId }
        });
        return response.data;
    } catch (error) {
        message.error(error.response.data[0])
    }
};

const getSentInvitations = async (pageNumber = 1, pageSize = 20) => {
    try {
        const response = await drEasyApi.get('/api/Invitations/getSendedInvitations', {
            params: { PageNumber: pageNumber, PageSize: pageSize }
        });
        return response.data;
    } catch (error) {
        console.error('Error fetching sent invitations:', error);
        throw new Error('Failed to fetch sent invitations. Please try again.');
    }
};

const cancelInvitation = async (invitationId) => {
    try {
        const response = await drEasyApi.delete('/api/Invitations/CancelInvitation', {
            params: { InvitationId: invitationId }
        });
        return response.data;
    } catch (error) {
        console.error('Error canceling invitation:', error);
        if (error.response && error.response.data) {
            throw new Error(error.response.data[0] || 'Failed to cancel invitation');
        }
        throw new Error('Failed to cancel invitation. Please try again.');
    }
};

const useResponsive = () => {
    const [breakpoint, setBreakpoint] = useState('lg');
    const [isMobile, setIsMobile] = useState(false);
    const [isTablet, setIsTablet] = useState(false);

    React.useEffect(() => {
        const updateBreakpoint = () => {
            const width = window.innerWidth;
            if (width < 640) {
                setBreakpoint('sm');
                setIsMobile(true);
                setIsTablet(false);
            } else if (width < 768) {
                setBreakpoint('md');
                setIsMobile(false);
                setIsTablet(true);
            } else if (width < 1024) {
                setBreakpoint('lg');
                setIsMobile(false);
                setIsTablet(true);
            } else if (width < 1280) {
                setBreakpoint('xl');
                setIsMobile(false);
                setIsTablet(false);
            } else {
                setBreakpoint('2xl');
                setIsMobile(false);
                setIsTablet(false);
            }
        };

        updateBreakpoint();
        window.addEventListener('resize', updateBreakpoint);
        return () => window.removeEventListener('resize', updateBreakpoint);
    }, []);

    return { breakpoint, isMobile, isTablet };
};

const DoctorCard = memo(({ doctor, index, onInvite, isInviting, isMobile }) => {
    const handleInviteClick = useCallback(() => {
        onInvite(doctor);
    }, [doctor, onInvite]);

    return (
        <div
            className={`group relative bg-white rounded-xl sm:rounded-2xl shadow-md hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 border border-gray-200 overflow-hidden ${isMobile ? 'mb-4' : ''}`}
            style={{
                animationDelay: `${index * 100}ms`,
                animation: 'fadeInUp 0.6s ease-out forwards'
            }}
        >
            <div className="p-4 sm:p-6 border-secondary/40 border radius-full" >
                <div className={`flex items-center mb-4 ${isMobile ? 'flex-wrap' : ''}`}>
                    <span className="p-[1px] border bg-primary rounded-full flex justify-center items-center">
                        <div className="w-16 h-16 sm:w-20 sm:h-20 bg-gradient-to-br from-primary to-primary_hover rounded-full flex items-center justify-center">
                            <img src={getImg(doctor.photoId)||"https://res.cloudinary.com/dk2uh7sho/image/upload/v1750278754/default-img_tbc2pe.jpg"} className="w-full h-full object-cover rounded-full" />
                        </div>
                    </span>
                    <div className="ml-3 sm:ml-4 min-w-0 flex-1">
                        <h3 className={`font-bold text-gray-800 group-hover:text-blue-600 transition-colors duration-300 ${isMobile ? 'text-base' : 'text-lg'} mb-1`}>
                            {doctor.name}
                        </h3>
                        {doctor.slug && (
                            <div className={`text-gray-500 ${isMobile ? 'text-xs' : 'text-sm'}`}>
                                🔗 {doctor.slug}
                            </div>
                        )}
                    </div>
                </div>

                {/* Doctor Details */}
                <div className={`p-3 sm:p-4 rounded-lg bg-gradient-to-r from-gray-50 to-blue-50 border border-gray-100 mb-4`}>
                    <div className="text-center">
                        <span className={`text-gray-600 font-medium ${isMobile ? 'text-xs' : 'text-sm'}`}>
                            👨‍⚕️ Doctor Profile Available
                        </span>
                    </div>
                </div>

                {/* Action Button */}
                {/* Status Badge - Only show for statuses 0, 1, 2 */}
                {(doctor.invitationStatus === 0 || doctor.invitationStatus === 1 || doctor.invitationStatus === 2) ? (
                    <div className="mt-2">
                        {doctor.invitationStatus === 0 && (
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                                <ClockCircleOutlined className="mr-1" />
                                Pending
                            </span>
                        )}
                        {doctor.invitationStatus === 1 && (
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                <CheckCircleOutlined className="mr-1" />
                                Accepted
                            </span>
                        )}
                        {doctor.invitationStatus === 2 && (
                            <div className='flex justify-between '>
                                <span className="inline-flex items-center px-6 py- rounded-full text-xs font-medium bg-red-200 text-red-800">
                                <CloseCircleOutlined className="mr-1" />
                                Rejected
                            </span>
                            <Button
                                type="primary"
                                icon={<UserAddOutlined />}
                                onClick={handleInviteClick}
                                loading={isInviting}
                                size={isMobile ? 'middle' : 'large'}
                                className={`bg-primary border-none rounded-full shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300 ${isMobile ? 'text-xs px-4' : 'px-6'}`}
                            >
                                    {isInviting ? 'Resending...' : 'Resend Invitation'}
                                </Button></div>
                        )}
                        {doctor.invitationStatus === 3 && (
                            <div className='flex justify-between '><span className="inline-flex items-center px-6 py- rounded-full text-xs font-medium bg-gray-200 text-gary-800">
                                <CloseCircleOutlined className="mr-1" />
                                Expired
                            </span><Button
                                type="primary"
                                icon={<UserAddOutlined />}
                                onClick={handleInviteClick}
                                loading={isInviting}
                                size={isMobile ? 'middle' : 'large'}
                                className={`bg-primary border-none rounded-full shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300 ${isMobile ? 'text-xs px-4' : 'px-6'}`}
                            >
                                    {isInviting ? 'Resending...' : 'Resend Invitation'}
                                </Button></div>
                        )}
                    </div>
                ) : (<div className="flex justify-end">
                    <Button
                        type="primary"
                        icon={<UserAddOutlined />}
                        onClick={handleInviteClick}
                        loading={isInviting}
                        size={isMobile ? 'middle' : 'large'}
                        className={`bg-primary border-none rounded-full shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300 ${isMobile ? 'text-xs px-4' : 'px-6'}`}
                    >
                        {isInviting ? 'Sending...' : 'Send Invitation'}
                    </Button>
                </div>)}

            </div>

            {/* Hover Effect Overlay */}
            <div className="absolute inset-0 bg-gradient-to-r from-blue-600/5 to-purple-600/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none rounded-xl sm:rounded-2xl"></div>
        </div>
    );
});

const InvitationModal = memo(({ visible, onCancel, onConfirm, doctor, loading, isMobile }) => (
    <Modal
        title={
            <div className="flex items-center">
                <div className={`${isMobile ? 'w-6 h-6' : 'w-8 h-8'} bg-primary  rounded-full flex items-center justify-center ${isMobile ? 'mr-2' : 'mr-3'}`}>
                    <MailOutlined className={`text-white ${isMobile ? 'text-xs' : 'text-sm'}`} />
                </div>
                <span className={isMobile ? 'text-sm' : ''}>Send Invitation</span>
            </div>
        }
        open={visible}
        onOk={onConfirm}
        onCancel={onCancel}
        okText="Send Invitation"
        cancelText="Cancel"
        confirmLoading={loading}
        width={isMobile ? '95vw' : 500}
        className="modern-modal"
        centered={isMobile}
    >
        {doctor && (
            <div className="py-4">
                <div className="flex items-center mb-4">
                    <Avatar
                        size={isMobile ? 48 : 64}
                        src={doctor.photoId ? getImg(doctor.photoId) : null}
                        icon={<UserOutlined />}
                        className="bg-primary"
                    />
                    <div className="ml-4">
                        <h3 className={`font-bold text-gray-800 ${isMobile ? 'text-base' : 'text-lg'}`}>
                            {doctor.name}
                        </h3>
                        <p className={`text-gray-600 ${isMobile ? 'text-sm' : ''}`}>
                            {doctor.specialization}
                        </p>
                        {doctor.slug && (
                            <p className={`text-gray-500 ${isMobile ? 'text-xs' : 'text-sm'}`}>
                                {doctor.slug}
                            </p>
                        )}
                    </div>
                </div>

                <Divider />

                <div className={`bg-blue-50 rounded-lg p-4 ${isMobile ? 'text-sm' : ''}`}>
                    <p className="text-blue-800 font-medium mb-2">
                        📨 You are about to send an invitation to Dr. {doctor.name}

                    </p>
                    <p className="text-blue-600">
                        The doctor will receive an email invitation to join your network and can accept or decline your request.
                    </p>
                </div>
            </div>
        )}
    </Modal>
));

const SendInvitationsTab = memo(({ isMobile, isTablet }) => {
    const [doctors, setDoctors] = useState([]);
    const [loading, setLoading] = useState(false);
    const [invitationModalVisible, setInvitationModalVisible] = useState(false);
    const [selectedDoctor, setSelectedDoctor] = useState(null);
    const [invitingDoctorId, setInvitingDoctorId] = useState(null);
    const [hasSearched, setHasSearched] = useState(false);

    const [filters, setFilters] = useState({
        name: '',
        specializationId: ''
    });

    const performSearch = useCallback(async (searchFilters) => {
        if (!searchFilters.name && !searchFilters.specializationId) {
            setDoctors([]);
            setHasSearched(false);
            return;
        }

        setLoading(true);
        setHasSearched(true);

        try {
            const results = await searchDoctors(searchFilters);
            setDoctors(results);
        } catch (error) {
            console.error('Search error:', error);
            setDoctors([]);
        } finally {
            setLoading(false);
        }
    }, []);

    React.useEffect(() => {
        const timeoutId = setTimeout(() => {
            performSearch(filters);
        }, 500);

        return () => clearTimeout(timeoutId);
    }, [filters, performSearch]);

    const handleNameChange = useCallback((e) => {
        const value = e.target.value;
        setFilters(prev => ({ ...prev, name: value }));
    }, []);

    const handleInviteClick = useCallback((doctor) => {
        setSelectedDoctor(doctor);
        setInvitationModalVisible(true);
    }, []);

    const handleInvitationConfirm = useCallback(async () => {
        if (!selectedDoctor) return;

        setInvitingDoctorId(selectedDoctor.id);

        try {
            await sendInvitation(selectedDoctor.id);
            message.success(`Invitation sent successfully to ${selectedDoctor.name}!`);
            setInvitationModalVisible(false);
            setSelectedDoctor(null);

            setDoctors(prev => prev.filter(doc => doc.id !== selectedDoctor.id));
        } catch (error) {
            console.error('Invitation error:', error);
            message.error(error.message || 'Failed to send invitation. Please try again.');
        } finally {
            setInvitingDoctorId(null);
        }
    }, [selectedDoctor]);

    const handleInvitationModalClose = useCallback(() => {
        setInvitationModalVisible(false);
        setSelectedDoctor(null);
    }, []);

    return (
        <div>
            <Card className={`mb-6 sm:mb-8 shadow-lg border-0 bg-white/90 backdrop-blur-sm ${isMobile ? 'mx-2' : ''}`}>
                <div className="w-full">
                    <Row gutter={[16, 16]} align="middle">
                        <Col xs={24}>
                            <div className="mb-2">
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Doctor Name
                                </label>
                                <Input
                                    placeholder="Start typing doctor's name..."
                                    value={filters.name}
                                    onChange={handleNameChange}
                                    prefix={<UserOutlined className="text-gray-400" />}
                                    size="large"
                                    className="rounded-lg"
                                    suffix={
                                        loading && (filters.name || filters.specializationId) ?
                                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div> :
                                            null
                                    }
                                />
                            </div>
                        </Col>
                    </Row>
                </div>
            </Card>

            <Spin spinning={false} tip="Searching for doctors...">
                {loading && (filters.name || filters.specializationId) && (
                    <div className="text-center py-8 mb-6">
                        <div className="inline-flex items-center px-4 py-2 bg-blue-50 rounded-lg">
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2"></div>
                            <span className="text-blue-600 text-sm">Searching...</span>
                        </div>
                    </div>
                )}

                {doctors.length > 0 ? (
                    <>
                        <div className="mb-4 text-center">
                            <span className={`text-gray-600 font-medium ${isMobile ? 'text-sm' : ''}`}>
                                📋 Found {doctors.length} doctor(s) matching your search
                            </span>
                        </div>
                        <div className={`grid grid-cols-1 ${isMobile ? 'gap-4' : isTablet ? 'sm:grid-cols-2 gap-4 sm:gap-6' : 'sm:grid-cols-2 lg:grid-cols-3 gap-6'}`}>
                            {doctors.map((doctor, index) => (
                                <DoctorCard
                                    key={doctor.id}
                                    doctor={doctor}
                                    index={index}
                                    onInvite={handleInviteClick}
                                    isInviting={invitingDoctorId === doctor.id}
                                    isMobile={isMobile}
                                />
                            ))}
                        </div>
                    </>
                ) : (
                    !loading && hasSearched && (
                        <div className="text-center py-16">
                            <Empty
                                image={Empty.PRESENTED_IMAGE_SIMPLE}
                                description={
                                    <span className={`text-gray-500 ${isMobile ? 'text-sm' : ''}`}>
                                        No doctors found matching your search criteria
                                    </span>
                                }
                            >
                                <Button
                                    type="primary"
                                    icon={<SearchOutlined />}
                                    onClick={() => {
                                        setHasSearched(false);
                                        setFilters({ name: '', specializationId: '' });
                                    }}
                                    className="bg-gradient-to-r from-blue-500 to-blue-600 border-none rounded-xl"
                                >
                                    Clear Search
                                </Button>
                            </Empty>
                        </div>
                    )
                )}

                {!hasSearched && !loading && (
                    <div className="text-center py-16">
                        <h3 className={`font-bold text-gray-600 mb-2 ${isMobile ? 'text-lg' : 'text-2xl'}`}>
                            Start Typing to Search!
                        </h3>
                        <p className={`text-gray-500 ${isMobile ? 'text-sm' : ''} max-w-md mx-auto`}>
                            Enter a doctor's name above. Results will appear automatically as you type.
                        </p>
                    </div>
                )}
            </Spin>

            {/* Invitation Confirmation Modal */}
            <InvitationModal
                visible={invitationModalVisible}
                onCancel={handleInvitationModalClose}
                onConfirm={handleInvitationConfirm}
                doctor={selectedDoctor}
                loading={invitingDoctorId === selectedDoctor?.id}
                isMobile={isMobile}
            />
        </div>
    );
});

// Sent Invitations Tab Component 
const SentInvitationsTab = memo(({ isMobile, isTablet }) => {
    const [sentInvitations, setSentInvitations] = useState([]);
    const [loading, setLoading] = useState(false);
    const [cancellingInvitationId, setCancellingInvitationId] = useState(null);
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 20,
        total: 0
    });

    // Fetch sent invitations
    const fetchSentInvitations = useCallback(async (pageNumber = 1, pageSize = 20) => {
        setLoading(true);
        try {
            const response = await getSentInvitations(pageNumber, pageSize);
            setSentInvitations(response.invitations);
            setPagination({
                current: response.pageNumber,
                pageSize: response.pageSize,
                total: response.totalCount
            });
        } catch (error) {
            console.error('Error fetching sent invitations:', error);
            message.error('Failed to fetch sent invitations');
        } finally {
            setLoading(false);
        }
    }, []);

    const handleCancelInvitation = useCallback(async (invitationId, doctorName) => {
        setCancellingInvitationId(invitationId);
        try {
            await cancelInvitation(invitationId);
            message.success(`Invitation to ${doctorName} has been cancelled successfully!`);
            
            setSentInvitations(prev => prev.filter(inv => inv.invitationId !== invitationId));
            
            setPagination(prev => ({
                ...prev,
                total: prev.total - 1
            }));
        } catch (error) {
            console.error('Error cancelling invitation:', error);
            message.error(error.message || 'Failed to cancel invitation. Please try again.');
        } finally {
            setCancellingInvitationId(null);
        }
    }, []);

    React.useEffect(() => {
        fetchSentInvitations();
    }, [fetchSentInvitations]);

    const getStatusDisplay = (status) => {
        switch (status.toLowerCase()) {
            case 'pending':
                return { color: 'orange', icon: <ClockCircleOutlined />, text: 'Pending' };
            case 'accepted':
                return { color: 'green', icon: <CheckCircleOutlined />, text: 'Accepted' };
            case 'declined':
                return { color: 'red', icon: <CloseCircleOutlined />, text: 'Declined' };
            case 'expired':
                return { color: 'gray', icon: <ExclamationCircleOutlined />, text: 'Expired' };
            default:
                return { color: 'default', icon: <ExclamationCircleOutlined />, text: status };
        }
    };

    return (
        <div>
            <div className="flex justify-between items-center mb-6">
                <div>
                    <h3 className={`font-bold text-gray-800 ${isMobile ? 'text-lg' : 'text-xl'} mb-1`}>
                        Invitations
                    </h3>
                    <p className={`text-gray-600 ${isMobile ? 'text-sm' : ''}`}>
                        Track the status of your sent doctor invitations
                    </p>
                </div>
                <Button
                    icon={<ReloadOutlined />}
                    onClick={() => fetchSentInvitations(pagination.current, pagination.pageSize)}
                    loading={loading}
                    className="rounded-lg"
                >
                    {!isMobile && 'Refresh'}
                </Button>
            </div>

            {/* Summary Cards */}
            <Row gutter={[16, 16]} className="mb-6">
                <Col xs={24} sm={8}>
                    <Card className="text-center bg-primary/10 border-primary/30">
                        <div className="text-primary text-2xl font-bold">
                            {pagination.total}
                        </div>
                        <div className={`text-primary font-medium ${isMobile ? 'text-sm' : ''}`}>
                            Total
                        </div>
                    </Card>
                </Col>
                <Col xs={24} sm={8}>
                    <Card className="text-center bg-orange-50 border-orange-200">
                        <div className="text-secondary text-2xl font-bold">
                            {sentInvitations.filter(inv => inv.status === 'Pending').length}
                        </div>
                        <div className={`text-secondary font-medium ${isMobile ? 'text-sm' : ''}`}>
                            Pending
                        </div>
                    </Card>
                </Col>
                <Col xs={24} sm={8}>
                    <Card className="text-center bg-green-50 border-green-200">
                        <div className="text-green-600 text-2xl font-bold">
                            {sentInvitations.filter(inv => inv.status === 'Accepted').length}
                        </div>
                        <div className={`text-green-800 font-medium ${isMobile ? 'text-sm' : ''}`}>
                            Accepted
                        </div>
                    </Card>
                </Col>
            </Row>

            {/* Invitations List */}
            {loading ? (
                <div className="text-center py-16">
                    <Spin />
                </div>
            ) : sentInvitations.length > 0 ? (
                <div className={`grid grid-cols-1 ${isMobile ? 'gap-4' : isTablet ? 'sm:grid-cols-2 gap-4 sm:gap-6' : 'sm:grid-cols-2 lg:grid-cols-3 gap-6'}`}>
                    {sentInvitations.map((invitation, index) => {
                        const { color, icon, text } = getStatusDisplay(invitation.status);
                        const formattedDate = new Date(invitation.createdDate).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                        });

                        const isPending = invitation.status.toLowerCase() === 'pending';
                        const isCurrentlyCancelling = cancellingInvitationId === invitation.invitationId;

                        return (
                            <div
                                key={invitation.invitationId}
                                className="group relative bg-white rounded-xl shadow-md hover:shadow-xl transition-all duration-300 border border-blue-200 overflow-hidden"
                                style={{
                                    animationDelay: `${index * 100}ms`,
                                    animation: 'fadeInUp 0.6s ease-out forwards'
                                }}
                            >
                                <div className="p-4 sm:p-6">
                                    <div className="flex items-center mb-4">
                                        <span className="p-[1px] border bg-primary rounded-full flex justify-center items-center">
                                            <div className="w-16 h-16 sm:w-20 sm:h-20 bg-gradient-to-br from-primary to-primary_hover rounded-full flex items-center justify-center">
                                                <img src={getImg(invitation.doctorPhoto)||"https://res.cloudinary.com/dk2uh7sho/image/upload/v1750278754/default-img_tbc2pe.jpg"} className="w-full h-full object-cover rounded-full" />
                                            </div>
                                        </span>
                                        <div className="ml-3 flex-1 min-w-0">
                                            <h3 className="font-bold text-gray-800 truncate">
                                                {invitation.doctorName}
                                            </h3>
                                            <p className="text-gray-500 text-sm truncate">{invitation.doctorEmail}</p>
                                        </div>
                                    </div>

                                    {/* Status + Date */}
                                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-3">
                                        <div className="flex justify-between items-start mb-2">
                                            <Tag color={color} icon={icon} className="rounded-full">
                                                {text}
                                            </Tag>
                                           
                                        </div>
                                        <div className="text-gray-500 text-xs">
                                            Sent on {formattedDate}
                                        </div>
                                    </div>

                                    {/* Action area for pending invitations */}
                                    {isPending && (
                                        <div className="mt-3 pt-3 border-t border-gray-100">
                                            <div className="flex justify-between items-center">
                                                <span className="text-xs text-gray-500">
                                                    Awaiting response...
                                                </span>
                                              {isPending && (
                                                <Popconfirm
                                                    title="Cancel Invitation"
                                                    description={`Are you sure you want to cancel the invitation to ${invitation.doctorName}?`}
                                                    onConfirm={() => handleCancelInvitation(invitation.invitationId, invitation.doctorName)}
                                                    okText="Yes, Cancel"
                                                    cancelText="No"
                                                    okButtonProps={{ danger: true }}
                                                    placement={isMobile ? "topRight" : "top"}
                                                >
                                                    <Tooltip title="Cancel Invitation">
                                                        <Button
                                                            type="text"
                                                            danger
                                                            icon={<StopOutlined />}
                                                            size="large"
                                                            loading={isCurrentlyCancelling}
                                                            className="hover:bg-red-50 rounded-full"
                                                        />
                                                    </Tooltip>
                                                </Popconfirm>
                                            )}
                                            </div>
                                        </div>
                                    )}
                                </div>

                                <div className="absolute inset-0 bg-gradient-to-r from-blue-600/5 to-purple-600/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none rounded-xl"></div>
                            </div>
                        );
                    })}
                </div>
            ) : (
                <Empty description="No invitations found" />
            )}
        </div>
    );
});

// Main Component
const InvitationsScreen = () => {
    const { isMobile, isTablet } = useResponsive();
    const [activeTab, setActiveTab] = useState('send');
const fromNotification = useLocation()?.state?.fromNotification || false;
useEffect(() => {
    if (fromNotification) {
        setActiveTab('sent');
    }},[])
    const tabItems = [
        {
            key: 'send',
            label: (
                <span className={`flex items-center ${isMobile ? 'text-sm' : ''}`}>
                    <UserAddOutlined className={`${isMobile ? 'mr-1' : 'mr-2'}`} />
                    {isMobile ? 'Send' : 'Send Invitations'}
                </span>
            ),
            children: <SendInvitationsTab isMobile={isMobile} isTablet={isTablet} />
        },
        {
            key: 'sent',
            label: (
                <span className={`flex items-center ${isMobile ? 'text-sm' : ''}`}>
                    <MailOutlined className={`${isMobile ? 'mr-1' : 'mr-2'}`} />
                    {isMobile ? 'Invitations' : 'Invitations'}
                </span>
            ),
            children: <SentInvitationsTab isMobile={isMobile} isTablet={isTablet} />
        }
    ];

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100">
            <style jsx>{`
                @keyframes fadeInUp {
                    from {
                        opacity: 0;
                        transform: translateY(30px);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }

                .modern-modal .ant-modal-body {
                    padding: ${isMobile ? '16px' : '24px'} !important;
                }

                @media (max-width: 640px) {
                    .ant-card-body {
                        padding: 16px !important;
                    }
                    .ant-tabs-tab {
                        padding: 8px 12px !important;
                    }
                }
            `}</style>

            <div className={`${isMobile ? 'p-4' : 'p-6'}`}>
                <PageTitle
                    title="👨‍⚕️ Doctor Invitations"
                    subtitle="Send invitations to doctors and track their responses"
                />

                <Card className="shadow-lg border-0 bg-white/90 backdrop-blur-sm">
                    <Tabs
                        activeKey={activeTab}
                        onChange={setActiveTab}
                        items={tabItems}
                        size={isMobile ? 'small' : 'large'}
                        className="modern-tabs"
                    />
                </Card>
            </div>
        </div>
    );
};

DoctorCard.displayName = 'DoctorCard';
InvitationModal.displayName = 'InvitationModal';
SendInvitationsTab.displayName = 'SendInvitationsTab';
SentInvitationsTab.displayName = 'SentInvitationsTab';

export default InvitationsScreen;